import { useAuth } from '@ab-core/hooks';
import { graphql, useStaticQuery } from 'gatsby';

type ContentfulResourceType = {
    allContentfulResource: {
        nodes?: Array<NodesType>;
    };
};

type NodesType = {
    value?: { value: string };
    key?: string;
};

const GLOBAL_ACCESS = '*';

export enum FEATURE_STATE {
    LOADING = 'loading',
    ALLOWED = 'allowed',
    DENIED = 'denied'
}

const FEATURE_ACCESS_INCLUDE_STRING = 'featureAccessList.';

export const useIsFeatureVisible = () => {
    const { allContentfulResource } = useStaticQuery<ContentfulResourceType>(
        graphql`
            query resourceQuery {
                allContentfulResource {
                    nodes {
                        value {
                            value
                        }
                        key
                    }
                }
            }
        `
    );
    const { nodes } = allContentfulResource;

    const featureAccessList: { [key: string]: Array<string> } = {};

    if (nodes) {
        for (const node of nodes) {
            const key = node.key;
            const value = node.value?.value;

            if (!key) {
                continue;
            }

            if (!value) {
                continue;
            }

            if (!key.includes(FEATURE_ACCESS_INCLUDE_STRING)) {
                continue;
            }

            const replacedKey = key.replace(FEATURE_ACCESS_INCLUDE_STRING, '');

            featureAccessList[replacedKey] = value.replaceAll(' ', '').split(',');
        }
    }

    const { user, userLoading } = useAuth();
    const accountNumber = user?.activeAccount?.accountNumber;

    const hasFeature = (featureName: string) => {
        return featureAccessList[featureName]?.some((access) => {
            if (access === GLOBAL_ACCESS) {
                return true;
            }

            return access === accountNumber;
        });
    };

    const isFeatureVisible = (featureName: string, isSite = false): boolean => {
        if (!featureName) {
            return true;
        }

        if (userLoading) {
            return !!isSite;
        }

        if (!accountNumber) {
            return false;
        }

        if (featureName) {
            return hasFeature(featureName);
        }

        return true;
    };

    const isFeatureVisibleOrLoading = (featureName: string): FEATURE_STATE => {
        if (userLoading) {
            return FEATURE_STATE.LOADING;
        }

        if (!accountNumber) {
            return FEATURE_STATE.DENIED;
        }

        const accountHasFeature = hasFeature(featureName);

        if (accountHasFeature) {
            return FEATURE_STATE.ALLOWED;
        }

        return FEATURE_STATE.DENIED;
    };

    return { isFeatureVisible, isFeatureVisibleOrLoading };
};
